import { faceNodeGetVolumeNode } from "building-generation/buildingGraph/faceNodeParent";
import {
  canonicalDirectionToResolvedSide,
  faceNodeOutwardDocFaceDirection,
} from "building-generation/buildingGraph/resolveFaceSide";
import { pushMissingProfileReferenceIssue } from "building-generation/elements/profileMissingReference";
import {
  getActiveProfileDescriptor,
  getProfileExtrusionDescriptorProfile,
} from "building-generation/elements/profileTools";
import { interiorWallPlaneTrimMoldingNew } from "building-generation/elements/trimMolding";
import { rebuildWallPlaneDescriptorWithAdditionalOperations } from "building-generation/elements/walls2/wallPlaneDescriptor";
import { defaultWallPlaneRules } from "building-generation/elements/walls2/wallPlaneRuleDefaults";
import type {
  BuildingGraph,
  BuildingGraphFaceNode,
  BuildingGraphPlane,
} from "building-types/building/buildingGraphTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import type { MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { ExteriorMaterialGeometry } from "building-types/geometry/exteriorMaterialGeometry";
import type { Level } from "building-types/levels/levelTypes";
import type { InteriorWallPlaneTrimMolding } from "building-types/molding/trimMoldingTypes";
import type { ProfileId } from "building-types/profiles/profileTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type {
  ResolvedWallPlaneSide,
  WallPlane,
  WallPlaneDescriptor,
  WallPlaneOpeningDomainOperationsByPlaneHash,
  WallPlaneSubdomain,
  WallPlaneSubdomainInfo,
} from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity } from "building-types/walls2/wallPlaneAssemblyTypes";
import type { DocFaceDirection } from "building-types/walls2/wallPlaneFilters";
import { isWallPlaneAnchoredRule, type WallPlaneRuleEntity } from "building-types/walls2/wallPlaneRules";
import type {
  WallPlaneTrimGeometry,
  WallPlaneTrimRail,
  ResolvedWallPlaneTrimContext,
  WallPlaneTrimEntity,
  WallPlaneTrimEntityBase,
} from "building-types/walls2/wallPlaneTrimTypes";
import { wallPlaneTrimFinishType } from "building-types/walls2/wallPlaneTrimTypes";
import type { Table, RWTable } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { builtInWallPlaneAssemblies } from "doc/defaults/defaultWallPlaneAssembly";
import { brepViewEntityNew } from "doc/utils/viewEntityTools";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { planeCopyMoveAlongNormal, planeNormal, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2AABB2, polyLine2EdgeCount, polyLine2EdgeEval, polyLine2Vertex } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeTangent, type PolyLine3 } from "geom/polyLine3";
import type { UVTransformation } from "geom/uv";
import { vec3CrossNormalize } from "geom/vec3";

export interface MakeWallPlaneTrimGenerationParams {
  readonly wallPlaneRules: Table<WallPlaneRuleEntity>;
  readonly wallPlanes: Table<WallPlane>;
  readonly buildingPlanes: Table<BuildingGraphPlane>;
  readonly buildingGraph: BuildingGraph;
  readonly profileSource: ActiveProfileSource;
  readonly wallPlaneOpeningDomainOperations: WallPlaneOpeningDomainOperationsByPlaneHash;
  readonly resolvedDatums: ResolvedDatumTable;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly masonryComponents: Table<MasonryComponentEntity>;
  readonly cipComponents: Table<CipComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
}

export interface MakeWallPlaneTrimsParams extends MakeWallPlaneTrimGenerationParams {
  readonly wallPlaneTrims: Table<WallPlaneTrimEntity>;
  readonly rooms: Table<Room>;
  readonly levels: Table<Level>;
}

export interface MakeWallPlaneTrimsResult {
  readonly wallPlaneTrims: Table<WallPlaneTrimGeometry>;
  readonly interiorWallPlaneTrimMoldings: Table<InteriorWallPlaneTrimMolding>;
  readonly issues: BuildingGenerationIssue[];
}

export function makeExteriorWallPlaneTrims(
  params: MakeWallPlaneTrimsParams,
  commonParams: MakeWallPlaneTrimCommonParams,
): Table<WallPlaneTrimGeometry> {
  const generatedWallPlaneTrims: RWTable<WallPlaneTrimGeometry> = {};

  for (const trim of Object.values(params.wallPlaneTrims)) {
    if (wallPlaneTrimFinishType(trim) !== "EXTERIOR") {
      continue;
    }
    const trimGeometry = makeExteriorWallPlaneTrim({
      trim,
      ...commonParams,
    });
    if (trimGeometry) {
      generatedWallPlaneTrims[trim.id] = trimGeometry;
    }
  }

  return generatedWallPlaneTrims;
}

export function makeInteriorWallPlaneTrimMoldings(
  params: MakeWallPlaneTrimsParams,
  commonParams: MakeWallPlaneTrimCommonParams,
): Table<InteriorWallPlaneTrimMolding> {
  const generatedInteriorWallPlaneTrimMoldings: RWTable<InteriorWallPlaneTrimMolding> = {};

  for (const trim of Object.values(params.wallPlaneTrims)) {
    if (wallPlaneTrimFinishType(trim) !== "INTERIOR") {
      continue;
    }
    const interiorMolding = makeInteriorWallPlaneTrim({
      trim,
      ...commonParams,
      buildingGraph: params.buildingGraph,
      rooms: params.rooms,
      levels: params.levels,
    });
    if (interiorMolding) {
      generatedInteriorWallPlaneTrimMoldings[trim.id] = interiorMolding;
    }
  }

  return generatedInteriorWallPlaneTrimMoldings;
}

export function makeWallPlaneTrims(params: MakeWallPlaneTrimsParams): MakeWallPlaneTrimsResult {
  const commonParams = makeWallPlaneTrimCommonParams(params);
  return {
    wallPlaneTrims: makeExteriorWallPlaneTrims(params, commonParams),
    interiorWallPlaneTrimMoldings: makeInteriorWallPlaneTrimMoldings(params, commonParams),
    issues: commonParams.issues,
  };
}

function makeWallPlaneTrimCommonParams({
  wallPlaneRules,
  wallPlanes,
  buildingPlanes,
  buildingGraph,
  profileSource,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  wallPlaneAssemblies,
  framingComponents,
  masonryComponents,
  cipComponents,
  sheathingComponents,
}: MakeWallPlaneTrimGenerationParams): MakeWallPlaneTrimCommonParams {
  return {
    resolvedRules: { ...defaultWallPlaneRules, ...wallPlaneRules },
    wallPlanes,
    buildingPlanes,
    wallPlaneDescriptorsByHash: resolveWallPlaneDescriptorsForTrimGeneration({
      wallPlanes,
      buildingGraph,
      wallPlaneOpeningDomainOperations,
      resolvedDatums,
      datumAbstractions,
      wallPlaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
    }),
    profileSource,
    issues: [],
  };
}

export interface MakeWallPlaneTrimCommonParams {
  readonly resolvedRules: Table<WallPlaneRuleEntity>;
  readonly wallPlanes: Table<WallPlane>;
  readonly buildingPlanes: Table<BuildingGraphPlane>;
  readonly wallPlaneDescriptorsByHash: Record<string, WallPlaneDescriptor>;
  readonly profileSource: ActiveProfileSource;
  readonly issues: BuildingGenerationIssue[];
}

function resolveTrimGenerationContext(
  trim: Pick<WallPlaneTrimEntityBase, "ruleId"> & { readonly id: string },
  { resolvedRules, wallPlanes, buildingPlanes, wallPlaneDescriptorsByHash }: MakeWallPlaneTrimCommonParams,
):
  | {
      readonly context: ResolvedWallPlaneTrimContext;
      readonly buildingPlane: BuildingGraphPlane;
      readonly wallPlaneDescriptor: WallPlaneDescriptor;
    }
  | undefined {
  const context = resolveWallPlaneTrimContext(trim, resolvedRules);
  if (!context) {
    return undefined;
  }

  const wallPlane = wallPlanes[context.planeHash];
  const buildingPlane = buildingPlanes[context.planeHash];
  const wallPlaneDescriptor = wallPlaneDescriptorsByHash[context.planeHash];
  if (!wallPlane || !buildingPlane || !wallPlaneDescriptor) {
    console.warn(`Wall plane trim "${trim.id}" references missing wall plane "${context.planeHash}".`);
    return undefined;
  }

  return { context, buildingPlane, wallPlaneDescriptor };
}

function makeExteriorWallPlaneTrim({
  trim,
  ...commonParams
}: MakeWallPlaneTrimCommonParams & {
  readonly trim: WallPlaneTrimEntity;
}): WallPlaneTrimGeometry | undefined {
  if (trim.disabled) {
    return undefined;
  }

  const resolved = resolveTrimGenerationContext(trim, commonParams);
  if (!resolved) {
    return undefined;
  }

  return makeExteriorWallPlaneTrimSweep({
    trim,
    context: resolved.context,
    wallPlaneDescriptor: resolved.wallPlaneDescriptor,
    buildingPlane: resolved.buildingPlane,
    profileSource: commonParams.profileSource,
    issues: commonParams.issues,
  });
}

function makeInteriorWallPlaneTrim({
  trim,
  buildingGraph,
  rooms,
  levels,
  ...commonParams
}: MakeWallPlaneTrimCommonParams & {
  readonly trim: WallPlaneTrimEntity;
  readonly buildingGraph: BuildingGraph;
  readonly rooms: Table<Room>;
  readonly levels: Table<Level>;
}): InteriorWallPlaneTrimMolding | undefined {
  if (trim.disabled) {
    return undefined;
  }

  const resolved = resolveTrimGenerationContext(trim, commonParams);
  if (!resolved) {
    return undefined;
  }

  return makeInteriorWallPlaneTrimSweep({
    trim,
    context: resolved.context,
    wallPlaneDescriptor: resolved.wallPlaneDescriptor,
    buildingPlane: resolved.buildingPlane,
    buildingGraph,
    rooms,
    levels,
    profileSource: commonParams.profileSource,
    issues: commonParams.issues,
  });
}

export function resolveWallPlaneTrimContext(
  trim: Pick<WallPlaneTrimEntityBase, "ruleId"> & { readonly id: string },
  wallPlaneRules: Table<WallPlaneRuleEntity>,
): ResolvedWallPlaneTrimContext | undefined {
  const rule = wallPlaneRules[trim.ruleId];
  if (!rule || rule.disabled) {
    console.warn(`Wall plane trim "${trim.id}" references missing or disabled rule "${trim.ruleId}".`);
    return undefined;
  }

  if (!isWallPlaneAnchoredRule(rule)) {
    console.warn(`Wall plane trim "${trim.id}" references rule "${trim.ruleId}" that is not plane-anchored.`);
    return undefined;
  }

  if (!rule.planeHash) {
    console.warn(`Wall plane trim "${trim.id}" references rule "${trim.ruleId}" with empty planeHash.`);
    return undefined;
  }

  const resolvedFaceSide = canonicalDirectionToResolvedSide(rule.direction);
  if (!resolvedFaceSide) {
    console.warn(`Wall plane trim "${trim.id}" could not resolve face side for rule "${trim.ruleId}".`);
    return undefined;
  }

  return {
    ruleId: rule.id,
    planeHash: rule.planeHash,
    resolvedFaceSide,
    operationBoundary: rule.operationBoundary,
  };
}

interface MakeExteriorWallPlaneTrimSweepParams {
  readonly trim: WallPlaneTrimEntity;
  readonly context: ResolvedWallPlaneTrimContext;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly profileSource: ActiveProfileSource;
  readonly issues: BuildingGenerationIssue[];
}

interface MakeInteriorWallPlaneTrimSweepParams {
  readonly trim: WallPlaneTrimEntity;
  readonly context: ResolvedWallPlaneTrimContext;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly buildingGraph: BuildingGraph;
  readonly rooms: Table<Room>;
  readonly levels: Table<Level>;
  readonly profileSource: ActiveProfileSource;
  readonly issues: BuildingGenerationIssue[];
}

function makeExteriorWallPlaneTrimSweep({
  trim,
  context,
  wallPlaneDescriptor,
  buildingPlane,
  profileSource,
  issues,
}: MakeExteriorWallPlaneTrimSweepParams): WallPlaneTrimGeometry | undefined {
  const sweepResult = makeExteriorWallPlaneTrimSweepSegments({
    trim,
    context,
    wallPlaneDescriptor,
    buildingPlane,
    profileSource,
    issues,
  });
  if (!sweepResult) {
    return undefined;
  }

  const { materialBreps, rails } = sweepResult;
  const geometry: ExteriorMaterialGeometry = {
    materialExtrusions: [],
    materialBreps,
    materialFaces: [],
  };

  return {
    sourceId: trim.id,
    ruleId: context.ruleId,
    planeHash: context.planeHash,
    resolvedFaceSide: context.resolvedFaceSide,
    geometry,
    rails,
  };
}

function makeInteriorWallPlaneTrimSweep({
  trim,
  context,
  wallPlaneDescriptor,
  buildingPlane,
  buildingGraph,
  rooms,
  levels,
  profileSource,
  issues,
}: MakeInteriorWallPlaneTrimSweepParams): InteriorWallPlaneTrimMolding | undefined {
  const sweepResult = makeInteriorWallPlaneTrimSweepSegments({
    trim,
    context,
    wallPlaneDescriptor,
    buildingPlane,
    profileSource,
    issues,
  });
  if (!sweepResult) {
    return undefined;
  }

  const { breps, trimRails, profileId, profileName } = sweepResult;
  const roomSet = collectRoomsOnTrimSideOfPath({
    path: trim.path,
    wallPlaneDescriptor,
    resolvedFaceSide: context.resolvedFaceSide,
    buildingGraph,
    rooms,
  });
  const levelId = resolveWallPlaneTrimLevelId(roomSet, levels);

  if (trim.finish?.finishType !== "INTERIOR") {
    return undefined;
  }
  const material = trim.finish.finishPalette;

  return interiorWallPlaneTrimMoldingNew({
    levelId,
    roomSet,
    trims: breps,
    material,
    trimRails,
    sourceId: trim.id,
    ruleId: context.ruleId,
    planeHash: context.planeHash,
    profileId,
    profileName,
    materialTransformation: resolveWallPlaneTrimMaterialTransformation(trim.materialTransformation),
  });
}

interface WallPlaneTrimSweepGeometryResult {
  readonly breps: ReturnType<typeof extrudeFaceAlongPolyLine3>[];
  readonly trimRails: PolyLine3[];
  readonly profileId: ProfileId;
  readonly profileName: string;
}

interface ExteriorWallPlaneTrimSweepSegmentsResult extends WallPlaneTrimSweepGeometryResult {
  readonly materialBreps: ExteriorMaterialGeometry["materialBreps"];
  readonly rails: WallPlaneTrimRail[];
}

function makeWallPlaneTrimSweepGeometry({
  path,
  profileId,
  context,
  wallPlaneDescriptor,
  buildingPlane,
  profileSource,
  issues,
  trimId,
}: {
  readonly path: PolyLine2;
  readonly profileId: ProfileId;
  readonly context: ResolvedWallPlaneTrimContext;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly profileSource: ActiveProfileSource;
  readonly issues?: BuildingGenerationIssue[];
  readonly trimId?: string;
}): WallPlaneTrimSweepGeometryResult | undefined {
  if (polyLine2EdgeCount(path) === 0) {
    return undefined;
  }

  const profileDescriptor = getActiveProfileDescriptor(profileSource, profileId);
  if (!profileDescriptor) {
    if (trimId) {
      pushMissingProfileReferenceIssue(
        issues,
        profileSource,
        profileId,
        { type: "WALL_PLANE_TRIM", id: trimId },
        "profileId",
      );
    }
    return undefined;
  }
  const sweepProfile = getProfileExtrusionDescriptorProfile(profileDescriptor);
  const canonicalPlane = buildingPlane.planeFace.plane;
  const segments = collectWallPlaneTrimPathSegments(path, wallPlaneDescriptor, context.resolvedFaceSide);
  if (segments.length === 0) {
    return undefined;
  }

  const breps: ReturnType<typeof extrudeFaceAlongPolyLine3>[] = [];
  const trimRails: PolyLine3[] = [];

  for (const segment of segments) {
    const sweepPlane = planeCopyMoveAlongNormal(canonicalPlane, segment.surfaceOffset);
    const path3 = polyLine2OnPlane(sweepPlane, segment.path);
    const [upX, upY, upZ] = getWallPlaneTrimExtrusionUp(canonicalPlane, path3, context.resolvedFaceSide);
    const brep = extrudeFaceAlongPolyLine3([sweepProfile], path3, upX, upY, upZ);
    if (!brep) {
      continue;
    }

    breps.push(brep);
    trimRails.push(path3);
  }

  if (breps.length === 0) {
    return undefined;
  }

  return {
    breps,
    trimRails,
    profileId,
    profileName: profileDescriptor.name ?? profileId,
  };
}

function makeExteriorWallPlaneTrimSweepSegments({
  trim,
  context,
  wallPlaneDescriptor,
  buildingPlane,
  profileSource,
  issues,
}: {
  readonly trim: WallPlaneTrimEntity;
  readonly context: ResolvedWallPlaneTrimContext;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly profileSource: ActiveProfileSource;
  readonly issues?: BuildingGenerationIssue[];
}): ExteriorWallPlaneTrimSweepSegmentsResult | undefined {
  if (trim.finish?.finishType !== "EXTERIOR") {
    return undefined;
  }
  const material = trim.finish.finishPalette;

  const sweepGeometry = makeWallPlaneTrimSweepGeometry({
    path: trim.path,
    profileId: trim.profileId,
    context,
    wallPlaneDescriptor,
    buildingPlane,
    profileSource,
    issues,
    trimId: trim.id,
  });
  if (!sweepGeometry) {
    return undefined;
  }

  const materialTransformation = resolveWallPlaneTrimMaterialTransformation(trim.materialTransformation);
  const materialBreps: ExteriorMaterialGeometry["materialBreps"] = [];
  const rails: WallPlaneTrimRail[] = [];

  for (let i = 0; i < sweepGeometry.breps.length; i++) {
    const brep = sweepGeometry.breps[i];
    const path3 = sweepGeometry.trimRails[i];
    materialBreps.push(
      brepViewEntityNew(
        brep,
        { type: "WALL_PLANE_TRIM", id: trim.id },
        undefined,
        material,
        undefined,
        materialTransformation,
      ),
    );
    rails.push({
      rail: path3,
      profileId: trim.profileId,
      profileName: sweepGeometry.profileName,
      material,
    });
  }

  return {
    ...sweepGeometry,
    materialBreps,
    rails,
  };
}

function makeInteriorWallPlaneTrimSweepSegments({
  trim,
  context,
  wallPlaneDescriptor,
  buildingPlane,
  profileSource,
  issues,
}: {
  readonly trim: WallPlaneTrimEntity;
  readonly context: ResolvedWallPlaneTrimContext;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly profileSource: ActiveProfileSource;
  readonly issues?: BuildingGenerationIssue[];
}): WallPlaneTrimSweepGeometryResult | undefined {
  return makeWallPlaneTrimSweepGeometry({
    path: trim.path,
    profileId: trim.profileId,
    context,
    wallPlaneDescriptor,
    buildingPlane,
    profileSource,
    issues,
    trimId: trim.id,
  });
}

function resolvedFaceSideToCanonicalDirection(resolvedFaceSide: ResolvedWallPlaneSide): DocFaceDirection {
  return resolvedFaceSide === "RIGHT" ? "POSITIVE_NORMAL" : "NEGATIVE_NORMAL";
}

/** A face node's volume lies on the side opposite its outward normal. */
function faceNodeIsOnSameSideAsTrimSegment(
  faceNode: BuildingGraphFaceNode,
  trimCanonicalDirection: DocFaceDirection,
): boolean {
  return faceNodeOutwardDocFaceDirection(faceNode) !== trimCanonicalDirection;
}

export function collectRoomsOnTrimSideOfPath({
  path,
  wallPlaneDescriptor,
  resolvedFaceSide,
  buildingGraph,
  rooms,
}: {
  readonly path: PolyLine2;
  readonly wallPlaneDescriptor: WallPlaneDescriptor;
  resolvedFaceSide: ResolvedWallPlaneSide;
  readonly buildingGraph: BuildingGraph;
  readonly rooms: Table<Room>;
}): RoomSet {
  const trimCanonicalDirection = resolvedFaceSideToCanonicalDirection(resolvedFaceSide);
  const pathAABB = polyLine2AABB2(path);
  const subdomains = wallPlaneDescriptor.subdomainIndex.search(pathAABB);
  const roomIds = new Set<string>();

  for (const subdomain of subdomains) {
    if (!subdomain.wallInfo) {
      continue;
    }
    for (const faceNodeId of subdomain.faceNodeIds) {
      const faceNode = buildingGraph.faceNodes[faceNodeId];
      if (!faceNode) {
        continue;
      }
      if (!faceNodeIsOnSameSideAsTrimSegment(faceNode, trimCanonicalDirection)) {
        continue;
      }
      const parentHandle = faceNodeGetVolumeNode(faceNode, buildingGraph)?.parentHandle;
      if (parentHandle?.type === "ROOM") {
        roomIds.add(parentHandle.roomId);
      }
    }
  }

  const roomSet: Room[] = [];
  for (const roomId of roomIds) {
    const room = rooms[roomId];
    if (room) {
      roomSet.push(room);
    }
  }

  roomSet.sort((a, b) => a.id.localeCompare(b.id));
  return roomSet;
}

function resolveWallPlaneTrimLevelId(roomSet: RoomSet, levels: Table<Level>): string {
  const room = roomSet[0];
  if (room) {
    return room.level.id;
  }

  const firstLevel = Object.values(levels)[0];
  assertIsNotNullOrUndefined(firstLevel);
  return firstLevel.id;
}

interface WallPlaneTrimPathSegment {
  readonly path: PolyLine2;
  readonly surfaceOffset: number;
}

type WallPlaneTrimPointClassification =
  | { readonly valid: false }
  | { readonly valid: true; readonly surfaceOffset: number };

interface WallPlaneTrimEdgeInterval {
  readonly t0: number;
  readonly t1: number;
  readonly surfaceOffset: number;
}

export function collectWallPlaneTrimPathSegments(
  path: PolyLine2,
  descriptor: WallPlaneDescriptor,
  resolvedFaceSide: ResolvedWallPlaneSide,
): WallPlaneTrimPathSegment[] {
  const edgeCount = polyLine2EdgeCount(path);
  if (edgeCount === 0) {
    return [];
  }

  const pathAABB = polyLine2AABB2(path);
  const subdomains = descriptor.subdomainIndex.search(pathAABB);

  const segments: WallPlaneTrimPathSegment[] = [];
  let currentPoints: number[] | undefined;
  let currentSurfaceOffset: number | undefined;

  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex++) {
    const [x0, y0] = polyLine2Vertex(path, edgeIndex);
    const [x1, y1] = polyLine2Vertex(path, edgeIndex + 1);
    const edgeIntervals = collectWallPlaneTrimEdgeIntervals({
      x0,
      y0,
      x1,
      y1,
      subdomains,
      descriptor,
      resolvedFaceSide,
    });

    for (const interval of edgeIntervals) {
      const [startX, startY] = polyLine2EdgeEval(path, edgeIndex, interval.t0, true);
      const [endX, endY] = polyLine2EdgeEval(path, edgeIndex, interval.t1, true);

      const intervalStartsNewSegment =
        currentPoints === undefined ||
        currentSurfaceOffset !== interval.surfaceOffset ||
        !wallPlaneTrimPathContinuesAt(currentPoints, startX, startY);

      if (intervalStartsNewSegment) {
        flushCurrentWallPlaneTrimPathSegment(segments, currentPoints, currentSurfaceOffset);
        currentPoints = [startX, startY, endX, endY];
        currentSurfaceOffset = interval.surfaceOffset;
        continue;
      }

      assertIsNotNullOrUndefined(currentPoints);
      const lastX = currentPoints[currentPoints.length - 2];
      const lastY = currentPoints[currentPoints.length - 1];
      if (Math.abs(lastX - startX) > DIST_EPSILON || Math.abs(lastY - startY) > DIST_EPSILON) {
        currentPoints.push(startX, startY);
      }
      if (Math.abs(lastX - endX) > DIST_EPSILON || Math.abs(lastY - endY) > DIST_EPSILON) {
        currentPoints.push(endX, endY);
      }
    }
  }

  flushCurrentWallPlaneTrimPathSegment(segments, currentPoints, currentSurfaceOffset);
  return segments;
}

function collectWallPlaneTrimEdgeIntervals({
  x0,
  y0,
  x1,
  y1,
  subdomains,
  descriptor,
  resolvedFaceSide,
}: {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly subdomains: readonly WallPlaneSubdomain[];
  readonly descriptor: WallPlaneDescriptor;
  readonly resolvedFaceSide: ResolvedWallPlaneSide;
}): WallPlaneTrimEdgeInterval[] {
  const candidateBreakpoints = collectWallPlaneTrimEdgeBreakpoints(x0, y0, x1, y1, subdomains);
  const breakpoints = filterWallPlaneTrimEdgeBreakpoints(
    candidateBreakpoints,
    x0,
    y0,
    x1,
    y1,
    descriptor,
    resolvedFaceSide,
  );
  const intervals: WallPlaneTrimEdgeInterval[] = [];

  for (let i = 0; i < breakpoints.length - 1; i++) {
    const t0 = breakpoints[i];
    const t1 = breakpoints[i + 1];
    if (t1 - t0 <= DIST_EPSILON) {
      continue;
    }

    const midT = (t0 + t1) / 2;
    const midX = x0 + (x1 - x0) * midT;
    const midY = y0 + (y1 - y0) * midT;
    const classification = classifyWallPlaneTrimPoint(descriptor, midX, midY, resolvedFaceSide);
    if (!classification.valid) {
      continue;
    }

    intervals.push({
      t0,
      t1,
      surfaceOffset: classification.surfaceOffset,
    });
  }

  return intervals;
}

function collectWallPlaneTrimEdgeBreakpoints(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  subdomains: readonly WallPlaneSubdomain[],
): number[] {
  const breakpoints = new Set<number>([0, 1]);
  const dx = x1 - x0;
  const dy = y1 - y0;

  for (const subdomain of subdomains) {
    if (Math.abs(dx) > DIST_EPSILON) {
      for (const x of [subdomain.minX, subdomain.maxX]) {
        const t = (x - x0) / dx;
        if (t > DIST_EPSILON && t < 1 - DIST_EPSILON) {
          breakpoints.add(t);
        }
      }
    }
    if (Math.abs(dy) > DIST_EPSILON) {
      for (const y of [subdomain.minY, subdomain.maxY]) {
        const t = (y - y0) / dy;
        if (t > DIST_EPSILON && t < 1 - DIST_EPSILON) {
          breakpoints.add(t);
        }
      }
    }
  }

  return [...breakpoints].sort((a, b) => a - b);
}

function filterWallPlaneTrimEdgeBreakpoints(
  candidateBreakpoints: readonly number[],
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  descriptor: WallPlaneDescriptor,
  resolvedFaceSide: ResolvedWallPlaneSide,
): number[] {
  if (candidateBreakpoints.length <= 2) {
    return [...candidateBreakpoints];
  }

  const essentialBreakpoints: number[] = [candidateBreakpoints[0]];

  for (let i = 1; i < candidateBreakpoints.length - 1; i++) {
    const leftT = (candidateBreakpoints[i - 1] + candidateBreakpoints[i]) / 2;
    const rightT = (candidateBreakpoints[i] + candidateBreakpoints[i + 1]) / 2;
    const leftX = x0 + (x1 - x0) * leftT;
    const leftY = y0 + (y1 - y0) * leftT;
    const rightX = x0 + (x1 - x0) * rightT;
    const rightY = y0 + (y1 - y0) * rightT;
    const leftClassification = classifyWallPlaneTrimPoint(descriptor, leftX, leftY, resolvedFaceSide);
    const rightClassification = classifyWallPlaneTrimPoint(descriptor, rightX, rightY, resolvedFaceSide);

    if (!wallPlaneTrimPointClassificationsEqual(leftClassification, rightClassification)) {
      essentialBreakpoints.push(candidateBreakpoints[i]);
    }
  }

  essentialBreakpoints.push(candidateBreakpoints[candidateBreakpoints.length - 1]);
  return essentialBreakpoints;
}

function wallPlaneTrimPointClassificationsEqual(
  left: WallPlaneTrimPointClassification,
  right: WallPlaneTrimPointClassification,
): boolean {
  if (!left.valid || !right.valid) {
    return left.valid === right.valid;
  }

  return Math.abs(left.surfaceOffset - right.surfaceOffset) <= DIST_EPSILON;
}

function wallPlaneTrimPathContinuesAt(points: number[], x: number, y: number): boolean {
  const lastX = points[points.length - 2];
  const lastY = points[points.length - 1];
  return Math.abs(lastX - x) <= DIST_EPSILON && Math.abs(lastY - y) <= DIST_EPSILON;
}

function classifyWallPlaneTrimPoint(
  descriptor: WallPlaneDescriptor,
  u: number,
  v: number,
  resolvedFaceSide: ResolvedWallPlaneSide,
): WallPlaneTrimPointClassification {
  const wallSubdomain = findWallPlaneSubdomainAtPoint(descriptor, u, v);
  if (!wallSubdomain?.wallInfo) {
    return { valid: false };
  }

  return {
    valid: true,
    surfaceOffset: getWallPlaneCladdingSurfaceOffset(wallSubdomain.wallInfo, resolvedFaceSide),
  };
}

function flushCurrentWallPlaneTrimPathSegment(
  segments: WallPlaneTrimPathSegment[],
  currentPoints: number[] | undefined,
  currentSurfaceOffset: number | undefined,
): void {
  if (currentPoints !== undefined && currentSurfaceOffset !== undefined) {
    segments.push({ path: polyLine2New(currentPoints), surfaceOffset: currentSurfaceOffset });
  }
}

export function findWallPlaneSubdomainAtPoint(
  descriptor: WallPlaneDescriptor,
  u: number,
  v: number,
): WallPlaneSubdomain | undefined {
  const candidates = descriptor.subdomainIndex.search({
    minX: u - DIST_EPSILON,
    minY: v - DIST_EPSILON,
    maxX: u + DIST_EPSILON,
    maxY: v + DIST_EPSILON,
  });

  for (const subdomain of candidates) {
    if (!subdomain.wallInfo) {
      continue;
    }
    if (u >= subdomain.minX && u <= subdomain.maxX && v >= subdomain.minY && v <= subdomain.maxY) {
      return subdomain;
    }
  }

  return undefined;
}

export function getWallPlaneCladdingSurfaceOffset(
  wallInfo: WallPlaneSubdomainInfo,
  resolvedFaceSide: ResolvedWallPlaneSide,
): number {
  const { coreThickness, wallOffset, leftCladdingThickness, rightCladdingThickness } = wallInfo;
  const halfCoreThickness = coreThickness / 2;
  const leftCoreOffset = halfCoreThickness - wallOffset;
  const rightCoreOffset = halfCoreThickness + wallOffset;
  const leftCladdingOffset = leftCoreOffset + leftCladdingThickness;
  const rightCladdingOffset = rightCoreOffset + rightCladdingThickness;

  if (resolvedFaceSide === "LEFT") {
    return -leftCladdingOffset;
  }
  return rightCladdingOffset;
}

export function getWallPlaneTrimSweepPlane(canonicalPlane: Plane, surfaceOffset: number): Plane {
  return planeCopyMoveAlongNormal(canonicalPlane, surfaceOffset);
}

/**
 * Board profiles place x=0 on the inside edge at the cladding surface and positive x
 * outward. After extrusion's internal reflect/reverse, depth extends along -u where
 * u = cross(up, tangent). Choose up so -u aligns with the outward cladding normal.
 *
 * The extrusion frame also defines default material UV axes (preserved with UVGeneration.NONE):
 * U runs along the sweep path, and V lies in the wall plane perpendicular to the path.
 */
export function getWallPlaneTrimExtrusionUp(
  canonicalPlane: Plane,
  path3: PolyLine3,
  resolvedFaceSide: ResolvedWallPlaneSide,
): [number, number, number] {
  const [nx, ny, nz] = planeNormal(canonicalPlane);
  const outwardSign = resolvedFaceSide === "RIGHT" ? 1 : -1;
  const outwardNormalX = nx * outwardSign;
  const outwardNormalY = ny * outwardSign;
  const outwardNormalZ = nz * outwardSign;
  const [tx, ty, tz] = polyLine3EdgeTangent(path3, 0);
  return vec3CrossNormalize(tx, ty, tz, -outwardNormalX, -outwardNormalY, -outwardNormalZ);
}

function resolveWallPlaneTrimMaterialTransformation(
  materialTransformation?: Partial<UVTransformation>,
): UVTransformation | undefined {
  if (!materialTransformation) {
    return undefined;
  }

  return {
    uTranslation: materialTransformation.uTranslation ?? 0,
    vTranslation: materialTransformation.vTranslation ?? 0,
    rotationDegrees: materialTransformation.rotationDegrees ?? 0,
  };
}

function resolveWallPlaneDescriptorsForTrimGeneration({
  wallPlanes,
  buildingGraph,
  wallPlaneOpeningDomainOperations,
  resolvedDatums,
  datumAbstractions,
  wallPlaneAssemblies,
  framingComponents,
  masonryComponents,
  cipComponents,
  sheathingComponents,
}: {
  readonly wallPlanes: Table<WallPlane>;
  readonly buildingGraph: BuildingGraph;
  readonly wallPlaneOpeningDomainOperations: WallPlaneOpeningDomainOperationsByPlaneHash;
  readonly resolvedDatums: ResolvedDatumTable;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly masonryComponents: Table<MasonryComponentEntity>;
  readonly cipComponents: Table<CipComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
}): Record<string, WallPlaneDescriptor> {
  const wallPlaneDescriptorsByHash: Record<string, WallPlaneDescriptor> = {};
  const resolvedWallPlaneAssemblies = {
    ...builtInWallPlaneAssemblies,
    ...wallPlaneAssemblies,
  };

  for (const planeHash in wallPlanes) {
    const wallPlane = wallPlanes[planeHash];
    if (!wallPlane) {
      continue;
    }

    wallPlaneDescriptorsByHash[planeHash] = rebuildWallPlaneDescriptorWithAdditionalOperations(
      wallPlane,
      buildingGraph,
      wallPlaneOpeningDomainOperations[planeHash] ?? [],
      resolvedDatums,
      datumAbstractions,
      resolvedWallPlaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
    );
  }

  return wallPlaneDescriptorsByHash;
}
